interface Player {
  id: number
  drop: number
  points: number
  raceId: number
  Person: {
    id: number
    avatar?: string
    fullName: string
    urlAvatar?: string
    phone?: string
    code: string
  }
}

interface Match {
  id: number
  table: number
  reported: boolean
  draw: boolean
  dropPlayer: boolean
  dropOpponent: boolean
  winnerVictories: number
  loserVictories: number
  Player: Player
  Opponent: Player
  Winner?: Player
  Loser?: Player
}

interface MatchTablePrintProps {
  matches: Match[]
  roundName: string
  tournamentName: string
  canSeeCode?: boolean
}

export const MatchTablePrint = ({
  matches,
  roundName,
  tournamentName,
  canSeeCode = true,
}: MatchTablePrintProps) => {
  // Create a comprehensive list of all players with their match details
  const getAllPlayersWithMatches = () => {
    const playersData: Array<{
      player: Player
      opponent?: Player
      table: number
      isReported: boolean
      isBye: boolean
    }> = []

    matches.forEach((match) => {
      // Add the main player
      playersData.push({
        player: match.Player,
        opponent: match.Opponent,
        table: match.table,
        isReported: match.reported,
        isBye: !match.Opponent,
      })

      // Add the opponent if exists
      if (match.Opponent) {
        playersData.push({
          player: match.Opponent,
          opponent: match.Player,
          table: match.table,
          isReported: match.reported,
          isBye: false,
        })
      }
    })

    // Remove duplicates and sort alphabetically by player name
    const uniquePlayers = playersData.filter(
      (playerData, index, arr) =>
        arr.findIndex((p) => p.player.id === playerData.player.id) === index,
    )

    return uniquePlayers.sort((a, b) =>
      a.player.Person.fullName.localeCompare(b.player.Person.fullName),
    )
  }

  const playersWithMatches = getAllPlayersWithMatches()

  return (
    <div className="print-container">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
        @page {
          margin: 0.1in;
        }
      `}</style>

      <div className="max-w-7xl mx-auto p-2 bg-white dark:bg-gray-900 dark:text-gray-100">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-1">{tournamentName}</h1>
          <h2 className="text-xl text-gray-600 dark:text-gray-300 mb-4">{roundName}</h2>
        </div>

        <div className="mb-4 text-center text-sm text-gray-600 dark:text-gray-400 no-print">
          <p>Para imprimir: Ctrl+P (PC) o Cmd+P (Mac)</p>
        </div>

        {/* Tabla */}
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-700 text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b border-border">
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Mesa
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Jugador
              </th>
              {canSeeCode && (
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Código
                </th>
              )}
              <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                vs
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Oponente
              </th>
              {canSeeCode && (
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Código
                </th>
              )}
              <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground no-print">
                Estado
              </th>
            </tr>
          </thead>
          <tbody>
            {playersWithMatches.map((playerData, index) => (
              <tr
                key={`${playerData.player.id}-${index}`}
                className="
                  border-b border-border
                  even:bg-muted/30
                  hover:bg-muted/60
                  transition-colors
                "
              >
                {/* Mesa */}
                <td className="px-1 py-1 text-center font-semibold text-sm">{playerData.table}</td>

                {/* Jugador */}
                <td
                  className={`
                    px-1 py-1 text-sm
                    ${
                      playerData.player.drop === 1
                        ? "line-through text-muted-foreground"
                        : "text-foreground"
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{playerData.player.Person.fullName}</span>
                    <span className="text-xs text-muted-foreground">
                      ({playerData.player.points} pts)
                    </span>
                  </div>
                </td>

                {/* Código jugador */}
                {canSeeCode && (
                  <td className="px-1 py-1 text-center text-xs font-mono text-muted-foreground">
                    {playerData.player.Person.code}
                  </td>
                )}

                {/* VS */}
                <td className="px-1 py-1 text-center text-muted-foreground font-bold">
                  {playerData.isBye ? "—" : "vs"}
                </td>

                {/* Oponente */}
                <td
                  className={`
                    px-1 py-1 text-sm
                    ${
                      playerData.opponent?.drop === 1
                        ? "line-through text-muted-foreground"
                        : "text-foreground"
                    }
                  `}
                >
                  {playerData.isBye ? (
                    <span className="italic text-muted-foreground">BYE</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{playerData.opponent?.Person.fullName}</span>
                      <span className="text-xs text-muted-foreground">
                        ({playerData.opponent?.points} pts)
                      </span>
                    </div>
                  )}
                </td>

                {/* Código oponente */}
                {canSeeCode && (
                  <td className="px-1 py-1 text-center text-xs font-mono text-muted-foreground">
                    {playerData.isBye ? "—" : playerData.opponent?.Person.code || ""}
                  </td>
                )}

                {/* Estado */}
                <td className="px-1 py-1 text-center no-print">
                  <span
                    className={`
                      inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium
                      ${
                        playerData.isReported
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                          : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      }
                    `}
                  >
                    {playerData.isReported ? "Reportado" : "Pendiente"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          Total de jugadores: {playersWithMatches.length} | Partidas: {matches.length} | Generado:{" "}
          {new Date().toLocaleString("es-ES")}
        </div>
      </div>
    </div>
  )
}
